import clearRecipientListChangedFlagAction from '../actions/clearRecipientListChangedFlagAction';
import '../mutators/smartComposeMutators';
import '../orchestrators/smartComposePluginOrchestrators';
import Recipients from '../store/schema/Recipients';
import SessionTelemetry from '../store/schema/SessionTelemetry';
import Suggestion, { SUGGESTION_CLASS } from '../store/schema/Suggestion';
import getStore from '../store/store';
import finalizeTelemetryForSession from '../telemetryHelpers/finalizeTelemetryForSession';
import initializeTelemetryForSession from '../telemetryHelpers/initializeTelemetryForSession';
import initializeTelemetryForSuggestion from '../telemetryHelpers/initializeTelemetryForSuggestion';
import updateRecentlyRejectedSuggestionsTelemetryWithCharacterTyped from '../telemetryHelpers/updateRecentlyRejectedSuggestionsTelemetryWithCharacterTyped';
import updateTelemetryForSessionWithExplicitFeedback from '../telemetryHelpers/updateTelemetryForSessionWithExplicitFeedback';
import updateTelemetryForSuggestionOnSuggestionRejected from '../telemetryHelpers/updateTelemetryForSuggestionOnSuggestionRejected';
import acceptSuggestion from '../utils/acceptSuggestion';
import {
    addFlightInformationToConstraintsEnUs,
    addFlightInformationToConstraintsIntl,
} from '../utils/addFlightInformationToConstraints';
import buildSessionInfo from '../utils/buildSessionInfo';
import initializeSuggestion from '../utils/initializeSuggestion';
import checkIfTypedCharacterMatchesCurrentSuggestion from '../utils/checkIfTypedCharacterMatchesCurrentSuggestion';
import createEnvelope, { transformEmailAddressWrapperToRecipient } from '../utils/createEnvelope';
import getNoFeedbackDialogParamsAssistanceCompose from '../utils/getNoFeedbackDialogParamsAssistanceCompose';
import getRecipientsFromRecipientWells from '../utils/getRecipientsFromRecipientWells';
import getSuggestionsFeedback from '../utils/getSuggestionsFeedback';
import handleSuggestionResponse from '../utils/handleSuggestionResponse';
import isKeycodePrintable from '../utils/isKeycodePrintable';
import logImplicitRejectionIfNecessary from '../utils/logImplicitRejectionIfNecessary';
import logRecentlyRejectedSuggestions from '../utils/logRecentlyRejectedSuggestions';
import logWarningIfCursorIsInsideSuggestionNode from '../utils/logWarningIfCursorIsInsideSuggestionNode';
import removeComposeOnlyContent from '../utils/removeComposeOnlyContent';
import showSuggestion from '../utils/showSuggestion';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { lazyLogSigsDatapoint } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FeedbackDialogParams, FeedbackCompoundCategory } from 'owa-feedback';
import loc from 'owa-localize';
import { lazyGetUserOptions } from 'owa-proofing-option';
import RecipientWellWithFindControlViewState from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import { getUserDisplayName, getUserEmailAddress } from 'owa-session-store';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import fetchPersonalizationFeedbackData from '../service/fetchPersonalizationFeedbackData';
import { getGuid } from 'owa-guid';
import { getDelegationTokenForOwa, getAccessTokenforResourceAsLazy } from 'owa-tokenprovider';
import { trace } from 'owa-trace';
import { createSmartComposeSession } from '@qas/smartcompose-tslibrary';
import { Recipient, ISuggestion, UserMetrics } from '@qas/smartcompose-tslibrary/dist/src/session';
import {
    SmartComposeInteraction,
    AcceptanceReason,
    RejectionReason,
} from '@qas/smartcompose-tslibrary/dist/src/smartComposeInteraction';
import { tabOutline } from 'owa-shared-post-boot';
import getSuggestionFromCache from '../utils/getSuggestionFromCache';
import {
    ComposeFeedbackViewState,
    lazyShowFeedbackRibbon,
    lazyHideFeedbackRibbon,
} from 'owa-compose-feedback';
import SmartComposeUI from '../components/SmartComposeUI';
import { CachedSuggestion } from '../store/schema/CachedSuggestion';
import { getCorrelationVector } from 'owa-service/lib/correlationVector';
import PluginUtils from 'owa-editor-plugin-utils/lib/schema/PluginUtils';
import EditorPluginEx from 'owa-editor-plugin-utils/lib/schema/EditorPluginEx';
import { EditorViewState, PluginViewStateAdapter, PluginWithUI } from 'owa-editor';
import {
    SmartComposeFeedbackRibbonPrompt,
    SmartComposeFeedbackRibbonPromptAfterFeedbackProvided,
} from './SmartComposePlugin.locstring.json';
import {
    addRecentlyRejectedSuggestionsTelemetry,
    removeCurrentlyShownSuggestionFromEditor,
    modifyCurrentlyShowSuggestionInEditor,
    fetchPersonalization,
    reportUsageForPersonalization,
    cacheSuggestion,
    clearSuggestionCache,
} from '../actions/smartComposePluginActions';
import {
    logSessionStardAndEnd,
    logSuggestionNotRendered,
    logSessionCreationFailed,
} from '../telemetryHelpers/logDatapoints';
import {
    IEditor,
    PluginDomEvent,
    PluginEvent,
    PluginEventType,
    ExtractContentWithDomEvent,
    QueryScope,
    BlockElement,
    GetContentMode,
} from 'roosterjs-editor-types';
import getTextPredictionEnabled from 'owa-proofing-option/lib/selectors/getTextPredictionEnabled';
import {
    getTextPredictionFlightState,
    TextPredictionRenderEnabled,
} from 'owa-proofing-option/lib/utils/getTextPredictionFlightState';
import { differenceInCalendarDays, owaDate } from 'owa-datetime';

const SESSION_END_REASON = 'unknown';
const SMART_COMPOSE_SERVICE_ENDPOINT = 'wss://fe-26.qas.bing.net/compose';
const SMART_COMPOSE_AUTH_ENDPOINT = 'https://fe-26.qas.bing.net/compose';
const STRIP_ZERO_WIDTH_CHARACTERS_REGEX = /[\u200B-\u200D\uFEFF]/g;
const NON_BREAKING_SPACE_REGEX = /[\xC2\xA0]/g;
const BODY_LENGTH = 4000;
const AUTH_TOKEN_REFRESH_TIMER = 2 * 60 * 1000; // 2 minutes
const IDLE_DEACTIVATE_TIMER = 2 * 60 * 1000; // 2 minutes
const SINGLE_SPACE = ' ';
const MSA_TOKEN_SCOPE = 'Compose.Send';
const FEEDBACK_RIBBON_HIDING_TIMEOUT = 5000;
const TWO_WEEKS = 14;

export default class SmartComposePlugin
    implements PluginWithUI<ComposeFeedbackViewState>, EditorPluginEx {
    private editor: IEditor;
    private pluginUtils: PluginUtils;
    private currentSmartComposeSession: any;
    private recipients: Recipients;
    private sessionTelemetry: SessionTelemetry;
    private shouldRenderSuggestions: boolean;
    private charactersTypedInSession: number;
    private refreshAuthTokenTimer: NodeJS.Timer;
    private idleTimer: NodeJS.Timer; // We need to disconnect the SmartCompose session after we have been idle
    private sessionDeactivatedBecauseOfIdle: boolean;
    private charactersBeforeCursor: string;
    private charactersBeforeCursorOnPreviousRequest: string;
    private previousSuggestionAccepted: boolean;
    private disposer: () => void;
    private observerDisposer: () => void;

    public getName() {
        return 'SmartCompose';
    }

    constructor(
        public readonly adapter: PluginViewStateAdapter<ComposeFeedbackViewState>,
        public readonly editorAdapter: PluginViewStateAdapter<EditorViewState>,
        private getAllRecipientsWells: () => RecipientWellWithFindControlViewState[],
        private getSubject: () => string,
        private composeOperation: number
    ) {
        this.shouldRenderSuggestions =
            (getTextPredictionFlightState() & TextPredictionRenderEnabled) !== 0;
    }

    public initialize(editor: IEditor) {
        // Only fetch the personalization if we're actually rendering suggestions
        if (this.shouldRenderSuggestions) {
            fetchPersonalization();
        }
        clearRecipientListChangedFlagAction();

        this.charactersTypedInSession = 0;
        this.previousSuggestionAccepted = false;
        this.editor = editor;
        this.recipients = { toRecipients: [], ccRecipients: [], bccRecipients: [] };
        this.updateRecpientListIfNecessary(true);
        this.initializeSmartComposeSession();

        this.disposer = this.editor.addDomEventHandler({
            blur: this.handleBlur,
        });

        lazyGetUserOptions.importAndExecute();
        clearSuggestionCache();
    }

    public dispose() {
        this.disposer();
        this.disposer = null;
        this.editor = null;
        tabOutline.disable = false;
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }

        this.endSmartComposeSession();

        if (this.observerDisposer) {
            this.observerDisposer();
            this.observerDisposer = null;
        }
    }

    private endSmartComposeSession = () => {
        logImplicitRejectionIfNecessary();
        logRecentlyRejectedSuggestions(this.currentSmartComposeSession);
        if (this.sessionTelemetry) {
            finalizeTelemetryForSession(
                this.sessionTelemetry,
                getStore().allSuggestionsTelemetry,
                SESSION_END_REASON,
                this.charactersTypedInSession
            );
            // log the session telemetry here. Log the session start here so we are sure we have complete information
            logSessionStardAndEnd(this.sessionTelemetry);
            reportUsageForPersonalization(this.sessionTelemetry);
        }

        if (this.refreshAuthTokenTimer) {
            clearInterval(this.refreshAuthTokenTimer);
            this.refreshAuthTokenTimer = null;
        }

        if (this.currentSmartComposeSession) {
            // Temprarily disable teminate session because of issue calling SIGS from the client.
            // See OM: 50467
            // this.currentSmartComposeSession.terminateSession();
            const sigsData = this.currentSmartComposeSession.getSignalInformation();
            sigsData.CustomProperties.dsatReported = this.sessionTelemetry.dsatReported
                ? this.sessionTelemetry.dsatReported
                : 0;
            sigsData.CustomProperties.satReported = this.sessionTelemetry.satReported
                ? this.sessionTelemetry.satReported
                : 0;
            lazyLogSigsDatapoint.importAndExecute('SmartComposeSummarized', {
                start: sigsData.StartTime,
                compliance: 'CustomerContent',
                customProperties: sigsData.CustomProperties,
            });

            this.currentSmartComposeSession.terminateSession();

            this.currentSmartComposeSession = null;
        }
    };

    public onPluginEvent(event: PluginEvent) {
        if (!getTextPredictionEnabled()) {
            return;
        }
        const domEvent = event as PluginDomEvent;

        // There are some events that we want to handle on keydown in order to prevent the default behaviour of the
        // keys and to make sure we show the suggestions as soon as the key is hit to make sure we don't have a weird
        // delay when showing suggestions.
        // In general we want to have all of the suggestion manipulation key events handled on key up so they don't happen
        // too fast and it feels natural
        if (event.eventType === PluginEventType.KeyDown) {
            this.updateIdleTimerAndReactivateSessionIfNeeded();

            const keyboardEvent = domEvent.rawEvent as KeyboardEvent;

            // Ignore events fired as part of IME composition
            if (keyboardEvent.isComposing || keyboardEvent.keyCode === 229) {
                return;
            }

            // First, check to see if we're showing a suggeston already. If we are showing a suggestion then
            // listen we need special handling for certain key events:
            // Arrow up/Arrow down/Tab: just prevent the default behavior so we don't move the insertion point
            // Escape: prevent default (so we don't close the editor) and then remove the suggestion
            const currentlyShownSuggestion = getCurrentlyShownSuggestion();
            if (currentlyShownSuggestion) {
                if (
                    keyboardEvent.keyCode === KeyCodes.tab ||
                    keyboardEvent.keyCode === KeyCodes.right
                ) {
                    event.rawEvent.preventDefault();
                    event.rawEvent.stopImmediatePropagation();
                } else if (
                    keyboardEvent.keyCode === KeyCodes.enter ||
                    keyboardEvent.keyCode === KeyCodes.del
                ) {
                    logImplicitRejectionIfNecessary();
                    removeCurrentlyShownSuggestionFromEditor(this.editor);
                } else if (keyboardEvent.keyCode === KeyCodes.escape) {
                    event.rawEvent.preventDefault();
                    event.rawEvent.stopImmediatePropagation();

                    // the user explicitly rejected the suggestion with the esc key, so log it and remove the suggestion
                    this.explicitRejection(currentlyShownSuggestion);
                }
            }

            // Next, check to see if the keycode is a printable character (i.e. alphanumberic + punctuation). If so,
            // check to see if we have any suggestions for auto-complete
            if (isKeycodePrintable(keyboardEvent.keyCode)) {
                this.charactersTypedInSession++;
                if (this.charactersBeforeCursor) {
                    this.charactersBeforeCursor += keyboardEvent.key;
                    this.checkForSuggestion(domEvent, keyboardEvent.key);
                }
            } else if (keyboardEvent.keyCode === KeyCodes.backspace) {
                if (keyboardEvent.ctrlKey || keyboardEvent.metaKey || keyboardEvent.altKey) {
                    this.explicitRejection(currentlyShownSuggestion);
                    this.charactersBeforeCursor = null;
                } else {
                    this.charactersBeforeCursor = this.getCharactersBeforeCursor(
                        BODY_LENGTH
                    )?.slice(0, -1);

                    this.checkForSuggestion(domEvent, keyboardEvent.key, true /*backspace*/);
                }
            }
        } else if (event.eventType === PluginEventType.KeyUp) {
            const keyboardEvent: KeyboardEvent = domEvent.rawEvent as KeyboardEvent;
            // These events are the events used to manipulate the suggestion, so they only take effect if we're
            // already showing a suggestion
            if (getCurrentlyShownSuggestion()) {
                if (
                    keyboardEvent.keyCode === KeyCodes.tab ||
                    keyboardEvent.keyCode === KeyCodes.right
                ) {
                    acceptSuggestion(
                        this.editor,
                        AcceptanceReason.ExplicitAccept,
                        this.currentSmartComposeSession,
                        keyboardEvent.keyCode
                    );
                    this.previousSuggestionAccepted = true;
                    event.rawEvent.preventDefault();
                    event.rawEvent.stopImmediatePropagation();
                    this.charactersBeforeCursor = null;
                } else if (
                    keyboardEvent.keyCode === KeyCodes.left ||
                    keyboardEvent.keyCode === KeyCodes.up ||
                    keyboardEvent.keyCode === KeyCodes.down
                ) {
                    logImplicitRejectionIfNecessary();
                    removeCurrentlyShownSuggestionFromEditor(this.editor);
                    this.charactersBeforeCursor = null;
                }
            }

            // We only want to flush and log the recently rejected suggestions when the user does something other than normal typing (i.e. if they hit
            // a non-pritable character other than shift)
            if (
                !isKeycodePrintable(keyboardEvent.keyCode) &&
                keyboardEvent.keyCode !== KeyCodes.shift &&
                keyboardEvent.keyCode !== KeyCodes.backspace
            ) {
                // any sort of non-printable keypress will invalidate any extra data we want to store on recently rejected suggestions, so log them now
                logRecentlyRejectedSuggestions(this.currentSmartComposeSession);
                this.charactersBeforeCursor = null;
            }

            this.cleanOrphanedSuggestionIfNecessary();
        } else if (event.eventType === PluginEventType.ExtractContentWithDom) {
            const extractContentEvent = event as ExtractContentWithDomEvent;
            removeComposeOnlyContent(extractContentEvent.clonedRoot);
        } else if (
            event.eventType === PluginEventType.MouseDown ||
            event.eventType === PluginEventType.ContentChanged ||
            event.eventType === PluginEventType.BeforePaste
        ) {
            logImplicitRejectionIfNecessary();
            removeCurrentlyShownSuggestionFromEditor(this.editor);
            // any sort of mouse event will invalidate any extra data we want to store on recently rejected suggestions
            logRecentlyRejectedSuggestions(this.currentSmartComposeSession);
            this.charactersBeforeCursor = null;
        } else if (event.eventType === PluginEventType.MouseUp) {
            this.charactersBeforeCursor = this.getCharactersBeforeCursor(BODY_LENGTH);
            this.checkForSuggestion(domEvent, '');
        }
    }

    private updateIdleTimerAndReactivateSessionIfNeeded() {
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
        }
        this.idleTimer = setTimeout(this.deactivateSessionBecauseIdle, IDLE_DEACTIVATE_TIMER);

        if (this.sessionDeactivatedBecauseOfIdle && this.currentSmartComposeSession) {
            trace.info('[SmartComposePlugin] reactivate smart compose session after idle');
            this.currentSmartComposeSession.activateSession();
            this.sessionDeactivatedBecauseOfIdle = false;
        }
    }

    private deactivateSessionBecauseIdle = () => {
        trace.info('[SmartComposePlugin] deactivate smart compose session because of idle');
        this.currentSmartComposeSession?.deactivateSession();
        this.sessionDeactivatedBecauseOfIdle = true;
    };

    private explicitRejection(currentlyShownSuggestion: Suggestion) {
        if (currentlyShownSuggestion) {
            updateTelemetryForSuggestionOnSuggestionRejected(
                currentlyShownSuggestion.suggestionTelemetry,
                RejectionReason.ExplicitRejection
            );
            addRecentlyRejectedSuggestionsTelemetry(currentlyShownSuggestion.suggestionTelemetry);
            removeCurrentlyShownSuggestionFromEditor(this.editor);
        }
    }

    public willHandleEventExclusively(event: PluginEvent) {
        return (
            getCurrentlyShownSuggestion() &&
            event.eventType === PluginEventType.KeyDown &&
            ((event.rawEvent as KeyboardEvent).keyCode === KeyCodes.tab ||
                (event.rawEvent as KeyboardEvent).keyCode === KeyCodes.right)
        );
    }

    public setPluginUtils(pluginUtils: PluginUtils) {
        this.pluginUtils = pluginUtils;
        if (this.pluginUtils) {
            this.observerDisposer = this.pluginUtils.observeMutation(
                this.processCEDContentChanged,
                {
                    subtree: true,
                    childList: false,
                    characterData: true,
                    characterDataOldValue: false,
                }
            );
        }
    }

    private processCEDContentChanged = (nodes: MutationRecord[]) => {
        if (nodes && nodes.length > 0) {
            if (!this.charactersBeforeCursor) {
                trace.info(
                    '[SmartComposePlugin] CED content changed and there is no cached characters before cursor, so refetch'
                );
                this.charactersBeforeCursor = this.getCharactersBeforeCursor(BODY_LENGTH);
            }
        }
    };

    public getUIComponentClass() {
        return SmartComposeUI;
    }

    private updateRecpientListIfNecessary(forceUpdate: boolean): boolean {
        if (
            (getStore().viewState.recipientListChanged || forceUpdate) &&
            this.getAllRecipientsWells
        ) {
            clearRecipientListChangedFlagAction();
            this.recipients = getRecipientsFromRecipientWells(this.getAllRecipientsWells());
            return true;
        }

        return false;
    }

    private async initializeSmartComposeSession() {
        this.sessionTelemetry = initializeTelemetryForSession(
            this.composeOperation,
            this.shouldRenderSuggestions
        );

        const constraints = new Map<string, string>();

        //Internationalization
        if (isFeatureEnabled('mc-smartComposeEnWW') || isFeatureEnabled('mc-smartComposeEs')) {
            constraints.set('scww', '1');
            if (isFeatureEnabled('mc-smartComposeEnWW')) {
                constraints.set('enww_prod', '1');
            }
            if (isFeatureEnabled('mc-smartComposeEs')) {
                constraints.set('es_prod', '1');
            }
            addFlightInformationToConstraintsIntl(constraints);
        } else {
            // The constraints here are used by the service to determine which model to use on the back end.
            // Here we're requesting the experimental model.
            constraints.set('compliant', '1');
            addFlightInformationToConstraintsEnUs(constraints);
        }

        const sessionInfo = buildSessionInfo(this.composeOperation);
        const authToken = await this.getAuthTokenForSmartCompose();
        this.currentSmartComposeSession = createSmartComposeSession(
            createEnvelope(
                this.getSubject(),
                this.recipients,
                new Recipient(getUserEmailAddress(), getUserDisplayName()),
                constraints
            ),
            SMART_COMPOSE_SERVICE_ENDPOINT,
            sessionInfo,
            authToken,
            [], // UrlParams
            this.onSuggestionReceived
        );

        // Set up a mechanism for refreshing the auth token for the websocket based session
        if (this.currentSmartComposeSession) {
            this.refreshAuthTokenTimer = setInterval(async () => {
                const authToken = await this.getAuthTokenForSmartCompose();
                if (authToken && this.currentSmartComposeSession) {
                    this.currentSmartComposeSession.updateAuthToken(authToken);
                }
            }, AUTH_TOKEN_REFRESH_TIMER);
            this.sendPersonalizationMetrics();
        }

        if (!this.currentSmartComposeSession) {
            logSessionCreationFailed();
        }
    }

    private async sendPersonalizationMetrics() {
        const response = await fetchPersonalizationFeedbackData();
        const aggregatedResult = {
            SAT: 0,
            DSAT: 0,
            SuggestionAccepted: 0,
            SuggestionRejected: 0,
        };
        const now = owaDate();
        if (response) {
            response.forEach(resp => {
                let { usageDetails } = resp;
                usageDetails.forEach(usage => {
                    const usageDate = owaDate('UTC', usage.dateTime);
                    if (differenceInCalendarDays(now, usageDate) <= TWO_WEEKS) {
                        aggregatedResult[resp.signal] += usage.count;
                    }
                });
            });
            const { SAT, DSAT, SuggestionAccepted, SuggestionRejected } = aggregatedResult;
            const userMetrics = new UserMetrics(SuggestionAccepted, SuggestionRejected, SAT, DSAT);
            this.currentSmartComposeSession.sendUserMetrics(userMetrics);
        }
    }

    private async getAuthTokenForSmartCompose(): Promise<string | undefined> {
        if (isConsumer()) {
            return getDelegationTokenForOwa(MSA_TOKEN_SCOPE, 'smartCompose');
        } else {
            let [token, tokenPromise] = getAccessTokenforResourceAsLazy(
                SMART_COMPOSE_AUTH_ENDPOINT,
                'smartCompose',
                getGuid()
            );

            if (!token) {
                token = (await tokenPromise) as string;
            }
            return token as string;
        }
    }

    // Given the characters before the cursor, query the service to get a new suggestion and return that suggestion
    private getNewSuggestion(
        charactersBeforeCursor: string,
        areCharactersAfterCursor: boolean,
        suggestionResponseHandler: (
            suggestionResponse: any,
            sessionTelemetry: SessionTelemetry,
            currentSmartComposeSession: any,
            currentSuggestionTelemetry: SmartComposeInteraction
        ) => Suggestion
    ) {
        if (charactersBeforeCursor && charactersBeforeCursor.length > 1) {
            // Check to see if the recipients have changed, update it if necessary
            const recipientListUpdated = this.updateRecpientListIfNecessary(
                false /* forceUpdate */
            );

            // Actually call the service to get the suggestions. Pass in whether or not the recipient list has
            // been updated so it knows if it needs to update the smart compose session
            this.getSuggestionsFromService(
                charactersBeforeCursor,
                areCharactersAfterCursor,
                recipientListUpdated,
                suggestionResponseHandler
            );
        }
    }

    private findAndShowNewSuggestion(event: PluginDomEvent, characterTyped: string) {
        if (!this.charactersBeforeCursor) {
            trace.info(
                '[SmartComposePlugin] Context not already available when finding suggestion. So, stopping'
            );
            return;
        }

        const charactersBeforeCursor: string = this.charactersBeforeCursor;

        // If we do need to get a new suggestion, first fetch the characters before the cursor and use that to get
        // a new suggestions
        const areCharactersAfterCursor = this.areCharactersAfterCursor(event);

        // check to see if the cache contains something good
        const suggestionFromCache = getSuggestionFromCache(charactersBeforeCursor);

        if (suggestionFromCache) {
            setTimeout(() => {
                this.onSuggestionReceived(suggestionFromCache);
            }, 1);
        } else {
            this.getNewSuggestion(
                charactersBeforeCursor,
                areCharactersAfterCursor,
                handleSuggestionResponse
            );
        }
    }

    private isCachedSuggestion(obj: any): obj is CachedSuggestion {
        return obj.context !== undefined && obj.suggestionTelemetry !== undefined;
    }

    /**
    This is called with an ISuggestion if it's called from the SmartCompose library, and it is called with a string
    if we're getting the suggestion from the cache
     */
    private onSuggestionReceived = (suggestion: ISuggestion | CachedSuggestion): void => {
        let newSuggestion: Suggestion;
        let charactersBeforeCursorAtTimeOfRequest: string;

        if (this.isCachedSuggestion(suggestion)) {
            charactersBeforeCursorAtTimeOfRequest = this.charactersBeforeCursor;
            newSuggestion = initializeSuggestion(
                suggestion.suggestion,
                null /* suggestionNode is null because the suggestion has not yet been inserted into the editor */,
                suggestion.suggestionTelemetry
            );
        } else if (suggestion) {
            if (!suggestion.context) {
                trace.info('[SmartComposePlugin] Suggestion context is missing');
                removeCurrentlyShownSuggestionFromEditor(this.editor);
                return;
            }

            newSuggestion = handleSuggestionResponse(
                suggestion,
                this.sessionTelemetry,
                this.currentSmartComposeSession,
                suggestion.context.currentSuggestionTelemetry
            );

            charactersBeforeCursorAtTimeOfRequest =
                suggestion.context.charactersBeforeCursorAtTimeOfRequest;
            const suggestionText = newSuggestion.suggestionText;
            if (suggestionText) {
                cacheSuggestion(
                    charactersBeforeCursorAtTimeOfRequest,
                    suggestionText,
                    suggestion.context.currentSuggestionTelemetry
                );
            }
        }

        if (newSuggestion?.suggestionText && this.shouldRenderSuggestions) {
            trace.info(
                `[SmartComposePlugin] onSuggestionReceived ${newSuggestion?.suggestionText}`
            );
            this.showFeedbackRibbon();

            if (getCurrentlyShownSuggestion()) {
                logSuggestionNotRendered(newSuggestion.suggestionTelemetry);
                return;
            }

            const currentCharactersBeforeCursor: string = this.getCharactersBeforeCursor(
                BODY_LENGTH
            );
            showSuggestion(
                this.editor,
                charactersBeforeCursorAtTimeOfRequest,
                newSuggestion,
                currentCharactersBeforeCursor,
                this.charactersTypedInSession,
                this.currentSmartComposeSession,
                this.editor.isDarkMode()
            );
        } else {
            removeCurrentlyShownSuggestionFromEditor(this.editor);
        }
    };

    private showFeedbackRibbon = () => {
        const chosenSuggestions: FeedbackCompoundCategory[] = getSuggestionsFeedback(
            this.sessionTelemetry.sessionId
        );

        const noFeedbackDialogParams: FeedbackDialogParams = getNoFeedbackDialogParamsAssistanceCompose(
            chosenSuggestions,
            this.editor?.getContent(GetContentMode.PlainText)
        );
        if (isFeatureEnabled('mc-smartComposeFeedback')) {
            lazyShowFeedbackRibbon.importAndExecute(this.adapter(), {
                ribbonMessage: loc(SmartComposeFeedbackRibbonPrompt),
                thankYouMessage: loc(SmartComposeFeedbackRibbonPromptAfterFeedbackProvided),
                subFeature: 'SmartCompose',
                noFeedbackDialogParams: noFeedbackDialogParams,
                onFeedbackSubmitted: this.onFeedbackSubmitted,
            });
        }
    };

    private onFeedbackSubmitted = (positiveFeedback: boolean) => {
        logRecentlyRejectedSuggestions(this.currentSmartComposeSession);
        updateTelemetryForSessionWithExplicitFeedback(this.sessionTelemetry, positiveFeedback);

        setTimeout(() => {
            lazyHideFeedbackRibbon.importAndExecute(this.adapter());
        }, FEEDBACK_RIBBON_HIDING_TIMEOUT);
    };

    private async checkForSuggestion(
        event: PluginDomEvent,
        characterTyped: string,
        backspace?: boolean
    ) {
        if (getCurrentlyShownSuggestion()) {
            if (backspace) {
                const suggestionFromCache = getSuggestionFromCache(this.charactersBeforeCursor);
                if (suggestionFromCache) {
                    modifyCurrentlyShowSuggestionInEditor(suggestionFromCache.suggestion);
                    return;
                }
            }
        }

        // Check to see if the character that was just typed matches the next character in the current suggestion
        const typedMatchingCharacter = checkIfTypedCharacterMatchesCurrentSuggestion(
            this.editor,
            characterTyped,
            this.currentSmartComposeSession
        );

        updateRecentlyRejectedSuggestionsTelemetryWithCharacterTyped(characterTyped);

        // If the next character typed does match the next character in the current suggestion then we don't need to
        // get a new suggestion.
        if (typedMatchingCharacter) {
            logWarningIfCursorIsInsideSuggestionNode();

            document.execCommand('insertText', false /* showUi */, characterTyped);
            event.rawEvent.preventDefault();
            return;
        }

        if (getCurrentlyShownSuggestion()) {
            removeCurrentlyShownSuggestionFromEditor(this.editor);
        }

        // If there are characters after the cursor then we don't want to make any requests, so just stop processing
        if (this.isThereACharacterAfterCursor(event)) {
            return;
        }

        this.findAndShowNewSuggestion(event, characterTyped);
    }

    private getSuggestionsFromService(
        charactersBeforeCursor: string,
        areCharactersAfterCursor: boolean,
        recipientListUpdated: boolean,
        suggestionResponseHandler: (
            suggestionResponse: any,
            sessionTelemetry: SessionTelemetry,
            currentSmartComposeSession: any,
            currentSuggestionTelemetry: SmartComposeInteraction
        ) => Suggestion
    ) {
        if (this.currentSmartComposeSession) {
            if (recipientListUpdated) {
                this.currentSmartComposeSession.updateSession(
                    this.getSubject(),
                    this.recipients.toRecipients.map(transformEmailAddressWrapperToRecipient)
                );
                clearSuggestionCache();
            }

            let currentSuggestionTelemetry = initializeTelemetryForSuggestion(
                this.sessionTelemetry,
                charactersBeforeCursor
            );

            const delta = this.getCharacterTypedDelta(charactersBeforeCursor);
            this.charactersBeforeCursorOnPreviousRequest = charactersBeforeCursor;
            const salutationAppropriate = areCharactersAfterCursor ? 0 : 1;

            // If we have a delta between the previous request and the current request then use the delta on the request,
            // otherwise fall back to using the entire context
            if (delta) {
                this.currentSmartComposeSession?.requestSuggestion(
                    charactersBeforeCursor,
                    salutationAppropriate,
                    this.previousSuggestionAccepted ? 1 : 0,
                    {
                        charactersBeforeCursorAtTimeOfRequest: charactersBeforeCursor,
                        currentSuggestionTelemetry: currentSuggestionTelemetry,
                    },
                    getCorrelationVector()
                );
            }

            this.previousSuggestionAccepted = false;
        }
    }

    /**
     * Gets the character(s) typed since the last request
     * @param charactersBeforeCursor The current characters before the cursor, or undefined if the previous request doesn't match the current context
     */
    private getCharacterTypedDelta(charactersBeforeCursor: string): string {
        // Get the index of the string of the previous request in the current request. If it's found then return the delta of what's been typed
        // since, otherwise return undefined
        const lastIndexOf = charactersBeforeCursor.lastIndexOf(
            this.charactersBeforeCursorOnPreviousRequest
        );

        if (lastIndexOf !== -1 && this.charactersBeforeCursorOnPreviousRequest) {
            return charactersBeforeCursor.substring(
                lastIndexOf + this.charactersBeforeCursorOnPreviousRequest.length
            );
        }
        return undefined;
    }

    private getCharactersBeforeCursor = (numberOfCharactersToGet: number): string => {
        if (this.editor !== null) {
            let content: string = '';

            // First get the characters immediately before the cursor.
            // The content searcher that we get from editor.getContentSearcherOfCursor only returns the characters
            // before the cursor within the current block
            const searcher = this.editor.getContentSearcherOfCursor();
            if (searcher) {
                content = searcher.getSubStringBefore(numberOfCharactersToGet);
            }

            // In order to get more text (beyond the current block element) use a body traverser starting at the
            // current block element and walk backwards adding on all of the text in each preceeding block element
            // until we get (roughly) to the numberOfCharactersToGet
            const blockTraverser = this.editor.getBlockTraverser();
            let currentBlockElement = blockTraverser?.currentBlockElement;
            if (currentBlockElement) {
                const bodyTraverser = this.editor.getBodyTraverser(
                    currentBlockElement.getStartNode()
                );
                while (content.length < numberOfCharactersToGet) {
                    // Here the bodyTraverser iterates though the block elements updating its internal state with the block
                    // element we're currently inspecting.
                    // Get the previouse BlockElement from the body if possible or null if we're at the beginning already
                    currentBlockElement = bodyTraverser.getPreviousBlockElement();
                    if (!currentBlockElement) {
                        break;
                    }

                    const textContent = currentBlockElement.getTextContent();
                    if (textContent) {
                        content = textContent + '\n' + content;
                    }
                }
            }

            content = content.replace(STRIP_ZERO_WIDTH_CHARACTERS_REGEX, '');
            content = content.replace(NON_BREAKING_SPACE_REGEX, SINGLE_SPACE);
            return content;
        }

        return '';
    };

    private areCharactersAfterCursor(event: PluginDomEvent): boolean {
        if (this.editor !== null) {
            // First get the inline element after the cursor. This will be set if the insertion point is in the middle of a block.
            // The inline element will cover the case where the insertion point is in the middle of a block and we need to get the
            // check for other characters in the block.
            // The body traverser will do the job of checking to see if other block elements exist
            const searcher = this.editor.getContentSearcherOfCursor(event);
            let elementAfterCursor = searcher?.getInlineElementAfter();
            if (elementAfterCursor) {
                const content = elementAfterCursor.getTextContent();
                if (content?.length > 0) {
                    return true;
                }
            } else {
                const focusedPosition = this.editor.getFocusedPosition();
                if (focusedPosition) {
                    const bodyTraverser = this.editor.getBodyTraverser(focusedPosition.element);
                    let nextElement: BlockElement = null;
                    try {
                        while ((nextElement = bodyTraverser.getNextBlockElement())) {
                            if (nextElement?.getTextContent()?.length > 0) {
                                return true;
                            }
                        }
                    } catch (error) {
                        trace.warn('[SmartComposePlugin] error getting characters after cursor');
                    }
                }
            }
        }
        return false;
    }

    private isThereACharacterAfterCursor(event: PluginDomEvent): boolean {
        const searcher = this.editor.getContentSearcherOfCursor(event);
        let elementAfterCursor = searcher?.getInlineElementAfter();
        return !!elementAfterCursor?.getTextContent();
    }

    // Check to see if there are any suggestion spans in the editor that don't match the currently shown suggestion. If there are
    // remove them
    private cleanOrphanedSuggestionIfNecessary() {
        // If we're not rendering suggestions, don't try to do anything
        if (!this.shouldRenderSuggestions) {
            return;
        }
        const currentlyShownSuggestion = getCurrentlyShownSuggestion();
        const currentlyShownSuggestionNode =
            currentlyShownSuggestion?.suggestionNode?.suggestionNode;
        const allSuggestions = this.editor.queryElements(`.${SUGGESTION_CLASS}`, QueryScope.Body);
        if (allSuggestions) {
            allSuggestions.forEach(suggestionNode => {
                if (suggestionNode !== currentlyShownSuggestionNode) {
                    // this suggestion node does not match the currently shown suggestion, so it is orphaned (probably because of an undo), so remove
                    // it from the dom
                    try {
                        suggestionNode.parentElement.removeChild(suggestionNode);
                    } catch (error) {
                        trace.warn('[SmartComposePlugin] error removing orphaned child node');
                    }
                }
            });
        }
    }

    private handleBlur = () => {
        removeCurrentlyShownSuggestionFromEditor(this.editor);
    };
}

function getCurrentlyShownSuggestion() {
    return getStore().viewState.currentlyShownSuggestion;
}
export { default as updateRecipientsForSmartCompose } from '../actions/updateRecipientsForSmartCompose';
